import React, { forwardRef, useId } from "react";
import { AlertCircle } from "lucide-react";
import {
  EndAdornmentSlot,
  FieldRoot,
  FieldLabel,
  HelperText,
  InputWithAdornmentWrap,
  LabelRow,
  RequiredMark,
  StyledInput,
} from "./FormField.styles";
import type { FormFieldProps } from "./FormField.types";

/** Auth spec §4: the field error leads with a 14px alert-circle. */
const AUTH_ERROR_ICON_SIZE = 14;

/**
 * Stateless label + input + helper/error composition. Renders a Figma-spec
 * config-form input (see {@link StyledInput}). Used by the Connector wizard's
 * credentials step and any future config-driven form.
 */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      label,
      required,
      helperText,
      errorMessage,
      type = "text",
      id,
      className,
      endAdornment,
      surface = "config",
      labelIcon,
      labelAction,
      ...inputProps
    },
    ref,
  ) => {
    const fallbackId = useId();
    const fieldId = id ?? `formfield-${fallbackId}`;
    const helperId = `${fieldId}-helper`;
    const hasError = Boolean(errorMessage);
    const labelElement = (
      <FieldLabel htmlFor={fieldId} $surface={surface}>
        {labelIcon}
        {label}
        {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
      </FieldLabel>
    );

    return (
      <FieldRoot className={className} $surface={surface} data-surface={surface}>
        {labelAction ? (
          <LabelRow>
            {labelElement}
            {labelAction}
          </LabelRow>
        ) : (
          labelElement
        )}
        {endAdornment ? (
          <InputWithAdornmentWrap>
            <StyledInput
              ref={ref}
              id={fieldId}
              type={type}
              $invalid={hasError}
              $surface={surface}
              $hasEndAdornment
              aria-required={required}
              aria-invalid={hasError || undefined}
              aria-describedby={helperText || errorMessage ? helperId : undefined}
              {...inputProps}
            />
            <EndAdornmentSlot $surface={surface}>{endAdornment}</EndAdornmentSlot>
          </InputWithAdornmentWrap>
        ) : (
          <StyledInput
            ref={ref}
            id={fieldId}
            type={type}
            $invalid={hasError}
            $surface={surface}
            aria-required={required}
            aria-invalid={hasError || undefined}
            aria-describedby={helperText || errorMessage ? helperId : undefined}
            {...inputProps}
          />
        )}
        {(errorMessage || helperText) && (
          <HelperText
            id={helperId}
            $error={hasError}
            $surface={surface}
            role={hasError ? "alert" : undefined}
          >
            {hasError && surface === "auth" ? (
              <AlertCircle size={AUTH_ERROR_ICON_SIZE} aria-hidden="true" />
            ) : null}
            {errorMessage || helperText}
          </HelperText>
        )}
      </FieldRoot>
    );
  },
);

FormField.displayName = "FormField";

export default FormField;
